import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { Profile, UserRole, PrimaryInterest } from '../types/database';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  // Contextual permission helpers
  isAuthenticated: boolean;
  needsOnboarding: boolean;
  // Legacy helpers - computed from contextual roles
  isCoach: boolean; // True if user is coach of any team
  isSpectator: boolean; // True if user has no team/org roles
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string, ageData?: {
    date_of_birth: string;
    is_minor: boolean;
    parent_email: string | null;
    parental_consent_given: boolean;
  }) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  uploadAvatar: (file: File) => Promise<{ url: string | null; error: Error | null }>;
  removeAvatar: () => Promise<{ error: Error | null }>;
  completeOnboarding: (interest: PrimaryInterest) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCoach, setIsCoach] = useState(false);
  const [isSpectator, setIsSpectator] = useState(true);

  const fetchProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
    return data;
  }, []);

  // Fetch user's contextual roles to determine isCoach/isSpectator
  const fetchUserRoles = useCallback(async (userId: string) => {
    let hasCoachRole = false;
    let hasAnyRole = false;

    // Check team_members for coach roles
    const { data: teamMembers } = await supabase
      .from('team_members')
      .select('role')
      .eq('user_id', userId);

    if (teamMembers && teamMembers.length > 0) {
      hasAnyRole = true;
      hasCoachRole = teamMembers.some(tm => 
        tm.role === 'head_coach' || tm.role === 'assistant_coach' || tm.role === 'manager'
      );
    }

    // Check if user is coach_id on any team (legacy)
    const { data: coachedTeams } = await supabase
      .from('teams')
      .select('id')
      .eq('coach_id', userId)
      .limit(1);

    if (coachedTeams && coachedTeams.length > 0) {
      hasCoachRole = true;
      hasAnyRole = true;
    }

    // Check organization memberships
    const { data: orgMembers } = await supabase
      .from('organization_members')
      .select('role')
      .eq('user_id', userId);

    if (orgMembers && orgMembers.length > 0) {
      hasAnyRole = true;
      // Org admins can also manage teams
      hasCoachRole = hasCoachRole || orgMembers.some(om => 
        om.role === 'owner' || om.role === 'admin'
      );
    }

    // Check if user owns any organization
    const { data: ownedOrgs } = await supabase
      .from('organizations')
      .select('id')
      .eq('owner_id', userId)
      .limit(1);

    if (ownedOrgs && ownedOrgs.length > 0) {
      hasCoachRole = true;
      hasAnyRole = true;
    }

    setIsCoach(hasCoachRole);
    setIsSpectator(!hasAnyRole);
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Add a timeout to prevent infinite loading on native apps with network issues
    const initTimeout = setTimeout(() => {
      if (isMounted) {
        console.warn('Auth initialization timed out after 10 seconds');
        setLoading(false);
      }
    }, 10000);

    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(initTimeout);
      if (!isMounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Fire-and-forget so a slow profile/role fetch never blocks the app
        // from flipping loading=false and rendering the current page.
        fetchProfile(session.user.id).then((data) => {
          if (isMounted) setProfile(data);
        });
        fetchUserRoles(session.user.id);
      }
      setLoading(false);
    }).catch((error) => {
      clearTimeout(initTimeout);
      console.error('Error getting session:', error);
      if (isMounted) setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id).then((data) => {
          if (isMounted) setProfile(data);
        });
        fetchUserRoles(session.user.id);
      } else {
        setProfile(null);
        setIsCoach(false);
        setIsSpectator(true);
      }
      setLoading(false);
    });

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
      subscription.unsubscribe();
    };
  }, [fetchProfile, fetchUserRoles]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName: string, ageData?: {
    date_of_birth: string;
    is_minor: boolean;
    parent_email: string | null;
    parental_consent_given: boolean;
  }) => {
    const { data, error } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        data: {
          full_name: fullName,
        }
      }
    });

    if (error) {
      return { error: error as Error };
    }

    if (!data.user) {
      return { error: null };
    }

    // signUp may omit a session depending on project auth settings; sign in so the client has a JWT for RLS and UI.
    if (!data.session) {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        return { error: signInError as Error };
      }
    }

    await new Promise(resolve => setTimeout(resolve, 500));

    const profileUpdate: Record<string, unknown> = { full_name: fullName };
    if (ageData) {
      profileUpdate.date_of_birth = ageData.date_of_birth;
      profileUpdate.is_minor = ageData.is_minor;
      profileUpdate.parent_email = ageData.parent_email;
      profileUpdate.parental_consent_given = ageData.parental_consent_given;
    }

    const { error: profileError } = await supabase.from('profiles')
      .update(profileUpdate)
      .eq('id', data.user.id);

    if (profileError) {
      console.warn('Profile update after signup:', profileError);
    }

    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setSession(null);
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) {
      return { error: new Error('No user logged in') };
    }

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (!error) {
      setProfile((prev) => prev ? { ...prev, ...updates } : null);
    }

    return { error: error as Error | null };
  };

  // Upload avatar to storage and update profile
  const uploadAvatar = async (file: File) => {
    if (!user) {
      return { url: null, error: new Error('No user logged in') };
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return { url: null, error: new Error('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.') };
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      return { url: null, error: new Error('File too large. Maximum size is 5MB.') };
    }

    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;

    // Delete old avatar if exists
    if (profile?.avatar_url) {
      const oldPath = profile.avatar_url.split('/avatars/')[1];
      if (oldPath) {
        await supabase.storage.from('avatars').remove([oldPath]);
      }
    }

    // Upload new avatar
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) {
      return { url: null, error: uploadError as Error };
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(fileName);

    // Update profile with new avatar URL
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: publicUrl })
      .eq('id', user.id);

    if (updateError) {
      return { url: null, error: updateError as Error };
    }

    // Update local state
    setProfile((prev) => prev ? { ...prev, avatar_url: publicUrl } : null);

    return { url: publicUrl, error: null };
  };

  // Remove avatar from storage and profile
  const removeAvatar = async () => {
    if (!user) {
      return { error: new Error('No user logged in') };
    }

    if (profile?.avatar_url) {
      // Extract path from URL
      const oldPath = profile.avatar_url.split('/avatars/')[1];
      if (oldPath) {
        await supabase.storage.from('avatars').remove([oldPath]);
      }
    }

    // Update profile to remove avatar URL
    const { error } = await supabase
      .from('profiles')
      .update({ avatar_url: null })
      .eq('id', user.id);

    if (!error) {
      setProfile((prev) => prev ? { ...prev, avatar_url: null } : null);
    }

    return { error: error as Error | null };
  };

  // Complete onboarding by setting primary interest
  const completeOnboarding = async (interest: PrimaryInterest) => {
    if (!user) {
      return { error: new Error('No user logged in') };
    }

    const { error } = await supabase
      .from('profiles')
      .update({ 
        primary_interest: interest,
        onboarding_completed: true,
      })
      .eq('id', user.id);

    if (!error) {
      setProfile((prev) => prev ? { 
        ...prev, 
        primary_interest: interest,
        onboarding_completed: true,
      } : null);
    }

    return { error: error as Error | null };
  };

  const isAuthenticated = !!user;
  const needsOnboarding = !!profile && !profile.onboarding_completed;

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        loading,
        isAuthenticated,
        needsOnboarding,
        isCoach,
        isSpectator,
        signIn,
        signUp,
        signOut,
        updateProfile,
        uploadAvatar,
        removeAvatar,
        completeOnboarding,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
